import styled from '@emotion/styled'
import Image from 'next/image'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AnalyticsContext } from '../../../../context/analyticsContext'
import { FullScreenVideoContext } from '../../../../context/fullScreenVideoContext'
import { VideoContext } from '../../../../context/viddyVideoContext'
import { getVideoManager } from '../../../../hooks/useHlsManager'
import { PlaylistPlayerVideo } from '../../../../types'
import {
    getVideoPosterUrl,
    videoToPlaylistPlayerVideo,
} from '../../../../utils/helpers'
import {
    CloseIcon,
    DownIcon,
    MutedIcon,
    UnmutedIcon,
} from '../../../icons/Icons'
import { LoadingScreen } from '../../../shared/LoadingScreen'
import { VideoProgressTrackerAndScrubber } from '../../../shared/VideoScrubber'
import { FullScreenContainer } from '../FullScreenContainer'

const ControlsContainer = styled.div({
    width: '100%',
    height: '100%',
    position: 'relative',
    borderRadius: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
})

const VideoContainer = styled.div({
    position: 'relative',
    borderRadius: 'inherit',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    overflow: 'hidden',
})

const VideoWrapper = styled.div({
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none',
    '& video': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        maxWidth: '100%',
        maxHeight: '100%',
    },
})

const TopControlsContainer = styled.div({
    zIndex: 2,
    width: '100%',
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
})

const BottomControlsContainer = styled.div({
    zIndex: 2,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    pointerEvents: 'auto',
})

const BottomButtonControlsContainerInner = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 16px 32px',
    boxSizing: 'border-box',
})

const CircularButtonContainer = styled.div({
    marginTop: 5,
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'black',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'auto',
})

const BottomRightControlsRow = styled.div({
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginBottom: 32,
})

type VideoNavButtonsProps = {
    onNextClick: () => void
    onPrevClick: () => void
    nextDisabled: boolean
    prevDisabled: boolean
}

const VideoNavButtons: React.FC<VideoNavButtonsProps> = ({
    onNextClick,
    onPrevClick,
    nextDisabled,
    prevDisabled,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
            }}
        >
            <CircularButtonContainer
                onClick={() => !prevDisabled && onPrevClick()}
                style={{
                    backgroundColor: prevDisabled
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(0,0,0,0.8)',
                }}
            >
                <Image
                    src={DownIcon}
                    width={37}
                    height={37}
                    alt=""
                    style={{
                        transform: 'rotate(180deg)',
                    }}
                />
            </CircularButtonContainer>
            <CircularButtonContainer
                onClick={() => !nextDisabled && onNextClick()}
                style={{
                    backgroundColor: nextDisabled
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(0,0,0,0.8)',
                    marginTop: 10,
                }}
            >
                <Image src={DownIcon} width={37} height={37} alt="" />
            </CircularButtonContainer>
        </div>
    )
}

const FullScreenVideoContainer = styled(FullScreenContainer)({
    inset: 0,
})

export const FullScreenVideoPlayer: React.FC<{ zIndex?: number }> = ({
    zIndex = 'unset',
}) => {
    const {
        isFullScreenPlayerOpen,
        setIsFullScreenPlayerOpen,
        currentIdx,
        currentPlaylist,
        setCurrentIdx,
        setCurrentPlaylist,
    } = useContext(FullScreenVideoContext)
    const { isMuted, setIsMuted, setIsBlurred } = useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const videoRef = useRef<HTMLVideoElement>(null)
    const videoManager = useRef(getVideoManager())
    const [playerVideo, setPlayerVideo] = useState<PlaylistPlayerVideo | null>(
        null,
    )

    useEffect(() => {
        if (
            currentIdx == -1 ||
            !currentPlaylist ||
            currentIdx > currentPlaylist.videos.length - 1
        ) {
            setPlayerVideo(null)
            return
        }
        const v = videoToPlaylistPlayerVideo(
            currentPlaylist.videos[currentIdx],
            currentPlaylist,
        )
        setPlayerVideo(v)
    }, [currentIdx, currentPlaylist])

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!playerVideo) {
            setIsFullScreenPlayerOpen(false)
            return
        }
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (playerVideo && !isFullScreenPlayerOpen) {
            setIsFullScreenPlayerOpen(true)
        }
        const videoElement = videoRef.current
        if (videoElement) {
            // Play video after HLS source is ready - fixes race condition where
            // autoPlay tries to play before HLS is attached (freezes on Chrome/Firefox)
            const handleCanPlay = () => {
                videoElement.play().catch(() => {})
                videoElement.removeEventListener('canplay', handleCanPlay)
            }
            videoElement.addEventListener('canplay', handleCanPlay)

            videoManager.current.attachInstance({
                videoRef,
                manifestUrl: `https://stream.mux.com/${playerVideo.playbackId}.m3u8`,
                playerVideo: playerVideo,
                videoManager: videoManager.current,
            })

            return () => {
                videoElement.removeEventListener('canplay', handleCanPlay)
            }
        }
    }, [playerVideo])

    const _handleVideoPlayPause = useCallback(
        (videoElement: HTMLVideoElement) => {
            if (!isFullScreenPlayerOpen) return
            setIsBlurred(false)

            if (videoElement.paused) {
                videoElement
                    .play()
                    .then(() => {})
                    .catch((e) => console.error(e))
            } else {
                videoElement.pause()
            }
        },
        [isFullScreenPlayerOpen, setIsBlurred],
    )

    const _onVideoClick = useCallback(
        (e: React.MouseEvent<HTMLVideoElement>) => {
            e.stopPropagation() // Prevent bubbling to container
            const videoElement = e.currentTarget
            _handleVideoPlayPause(videoElement)
        },
        [_handleVideoPlayPause],
    )

    const _onCloseClick = () => {
        const videoElement = videoRef.current
        if (!videoElement) return

        videoElement.pause()
        setIsFullScreenPlayerOpen(false)
        setCurrentPlaylist(null)
        setCurrentIdx(-1)
    }

    const _onNextClick = () => {
        if (currentIdx === -1 || !currentPlaylist) return
        if (currentIdx === currentPlaylist.videos.length - 1) return
        setCurrentIdx(currentIdx + 1)
    }

    const _onPrevClick = () => {
        if (currentIdx === -1 || !currentPlaylist) return
        if (currentIdx === 0) return
        setCurrentIdx(currentIdx - 1)
    }

    const toggleMute = () => {
        setIsMuted(!isMuted)
        const newMutedState = !isMuted
        Analytics.TRACK_VIDEO_PLAYER_MUTE_TOGGLE(
            newMutedState ? 'muted' : 'unmuted',
            'full_screen_icon_click',
        )
    }

    const showNavButton = Boolean(
        currentPlaylist && currentPlaylist.videos.length > 1,
    )
    const blurSrc =
        currentIdx >= 0 && currentPlaylist
            ? getVideoPosterUrl(currentPlaylist.videos[currentIdx])
            : null

    return (
        <FullScreenVideoContainer
            style={{
                display: isFullScreenPlayerOpen ? 'inline' : 'none',
                overflow: 'hidden',
                zIndex,
            }}
        >
            {blurSrc && (
                <Image
                    loading="lazy"
                    width={700}
                    height={700}
                    style={{
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                        filter: 'blur(35px)',
                        position: 'absolute',
                        zIndex: -1,
                    }}
                    src={blurSrc}
                    alt=""
                />
            )}

            <VideoContainer>
                {playerVideo && (
                    <VideoProgressTrackerAndScrubber
                        playerVideo={playerVideo}
                        videoRef={videoRef}
                    />
                )}
                <ControlsContainer>
                    <TopControlsContainer>
                        <div
                            style={{
                                marginRight: 10,
                                marginTop: 20,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularButtonContainer
                                className="control-element"
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.75)',
                                }}
                                onClick={_onCloseClick}
                            >
                                <Image
                                    src={CloseIcon}
                                    width={22}
                                    height={22}
                                    alt=""
                                />
                            </CircularButtonContainer>
                        </div>
                    </TopControlsContainer>
                    <BottomControlsContainer>
                        <BottomButtonControlsContainerInner>
                            <BottomRightControlsRow>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        gap: 12,
                                    }}
                                >
                                    <CircularButtonContainer
                                        className="control-element"
                                        onClick={toggleMute}
                                    >
                                        {isMuted ? (
                                            <Image
                                                width={22}
                                                height={22}
                                                src={MutedIcon}
                                                alt=""
                                            />
                                        ) : (
                                            <Image
                                                width={22}
                                                height={22}
                                                src={UnmutedIcon}
                                                alt=""
                                            />
                                        )}
                                    </CircularButtonContainer>
                                    {showNavButton && (
                                        <VideoNavButtons
                                            onNextClick={_onNextClick}
                                            onPrevClick={_onPrevClick}
                                            nextDisabled={
                                                currentIdx ===
                                                (currentPlaylist
                                                    ? currentPlaylist.videos
                                                          .length - 1
                                                    : 0)
                                            }
                                            prevDisabled={currentIdx === 0}
                                        />
                                    )}
                                </div>
                            </BottomRightControlsRow>
                        </BottomButtonControlsContainerInner>
                    </BottomControlsContainer>
                </ControlsContainer>
                <LoadingScreen />
                <VideoWrapper>
                    <video
                        playsInline={true}
                        preload={'metadata'}
                        muted={isMuted}
                        controls={false}
                        poster={playerVideo?.poster}
                        onEnded={_onNextClick}
                        ref={videoRef}
                        onClick={_onVideoClick}
                    ></video>
                </VideoWrapper>
            </VideoContainer>
        </FullScreenVideoContainer>
    )
}
